import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  FormControlLabel,
  Switch,
  Stack,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Divider,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { fetchData } from "../../api/client";
import { clientFormSectionBodySx, clientFormSectionHeaderSx, clientFormSubsectionTitleSx } from "./clientFormStyles";
import { usePortalClienteChannelConfigured } from "../../hooks/usePortalClienteChannelConfigured";

function buildPortalPublicUrl(companySlug) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const slug = String(
    companySlug ||
      (typeof window !== "undefined" ? window.localStorage.getItem("company") : "") ||
      ""
  ).trim();
  if (!slug) return `${origin}/portal-cliente`;
  return `${origin}/portal-cliente?company=${encodeURIComponent(slug)}`;
}

function ModuleSwitch({ checked, onChange, disabled, title, description }) {
  return (
    <FormControlLabel
      control={
        <Switch checked={Boolean(checked)} onChange={onChange} color="primary" disabled={disabled} />
      }
      label={
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
            {description}
          </Typography>
        </Box>
      }
      sx={{ alignItems: "flex-start", ml: 0, mr: 0 }}
    />
  );
}

export default function ClientPortalSection({
  clientId,
  email,
  acessoPortal,
  liberarSolicitacoes = true,
  liberarTarefas = false,
  hasPassword,
  onAcessoChange,
  onModulesChange,
  companySlug,
  compact = false,
  accessSaving = false,
  sectionCardSx,
  embeddedInTab = false,
}) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [copied, setCopied] = useState(false);
  const [modulesSaving, setModulesSaving] = useState(false);

  const { configured: portalChannelConfigured, isLoading: portalChannelLoading } =
    usePortalClienteChannelConfigured();

  const portalUrl = useMemo(() => buildPortalPublicUrl(companySlug), [companySlug]);

  useEffect(() => {
    setErr(null);
    setMsg(null);
  }, [acessoPortal, liberarSolicitacoes, liberarTarefas]);

  const statusChip = useMemo(() => {
    if (!acessoPortal) return { label: "Sem acesso", color: "default" };
    if (hasPassword) return { label: "Acesso ativo", color: "success" };
    return { label: "Aguardando senha", color: "warning" };
  }, [acessoPortal, hasPassword]);

  const handleInvite = async () => {
    if (!clientId) {
      setErr("Salve o cadastro antes de enviar o convite.");
      return;
    }
    if (!acessoPortal) {
      setErr("Ative o acesso ao portal antes de enviar o convite.");
      return;
    }
    if (!email?.trim()) {
      setErr("Informe um e-mail de contato para enviar o convite.");
      return;
    }
    if (!liberarSolicitacoes && !liberarTarefas) {
      setErr("Libere ao menos solicitações ou tarefas antes de enviar o convite.");
      return;
    }
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      await fetchData(`/clients/${clientId}/portal/invite`, "POST", {});
      setMsg("Convite enviado. O cliente receberá um e-mail para definir a senha.");
    } catch (e) {
      const apiMsg = e?.message || e?.data?.message || "";
      if (String(apiMsg).includes("Libere o acesso")) {
        setErr("Confirme o acesso ao portal antes de enviar o convite.");
      } else {
        setErr(apiMsg || "Não foi possível enviar o convite.");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleToggleAccess = async (event) => {
    const next = event.target.checked ? 1 : 0;
    setErr(null);
    try {
      await onAcessoChange?.(next);
    } catch {
      // Erro tratado pelo formulário pai
    }
  };

  const persistModules = async (nextSolicitacoes, nextTarefas) => {
    if (!onModulesChange) return;
    if (!nextSolicitacoes && !nextTarefas && acessoPortal) {
      setErr("Mantenha ao menos um módulo liberado (solicitações ou tarefas).");
      return;
    }
    setModulesSaving(true);
    setErr(null);
    try {
      await onModulesChange({
        portal_liberar_solicitacoes: nextSolicitacoes ? 1 : 0,
        portal_liberar_tarefas: nextTarefas ? 1 : 0,
      });
    } catch (e) {
      setErr(e?.message || "Não foi possível atualizar os módulos do portal.");
    } finally {
      setModulesSaving(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(portalUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setErr("Não foi possível copiar o link.");
    }
  };

  if (portalChannelLoading || !portalChannelConfigured) {
    return null;
  }

  const inner = (
    <Stack spacing={2.25}>
      <Box>
        <FormControlLabel
          control={
            <Switch
              checked={Boolean(acessoPortal)}
              onChange={handleToggleAccess}
              color="primary"
              disabled={accessSaving}
            />
          }
          label={
            <Box>
              <Typography variant="body2" fontWeight={600}>
                Liberar acesso ao Portal do Cliente
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                Permite login do cliente com e-mail e senha. Defina abaixo o que ele poderá usar.
              </Typography>
            </Box>
          }
          sx={{ alignItems: "flex-start", ml: 0, mr: 0 }}
        />
        {accessSaving ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, ml: 6 }}>
            Atualizando permissão…
          </Typography>
        ) : null}
      </Box>

      {acessoPortal ? (
        <Box
          sx={{
            pl: { xs: 0, sm: 1 },
            borderLeft: { sm: "2px solid" },
            borderColor: { sm: "divider" },
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={700}
            sx={{ textTransform: "uppercase", letterSpacing: 0.4, display: "block", mb: 1.25 }}
          >
            Módulos liberados
          </Typography>
          <Stack spacing={1.5}>
            <ModuleSwitch
              checked={liberarSolicitacoes}
              disabled={modulesSaving || accessSaving || !onModulesChange}
              onChange={(e) => persistModules(e.target.checked, liberarTarefas)}
              title="Solicitações"
              description="Listar, abrir e acompanhar chamados pelo portal."
            />
            <ModuleSwitch
              checked={liberarTarefas}
              disabled={modulesSaving || accessSaving || !onModulesChange}
              onChange={(e) => persistModules(liberarSolicitacoes, e.target.checked)}
              title="Tarefas"
              description="Kanban do painel do cliente, com status e conversa nas tarefas."
            />
          </Stack>
          {modulesSaving ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              Salvando módulos…
            </Typography>
          ) : null}
        </Box>
      ) : null}

      {!clientId && acessoPortal ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          Salve o cadastro para confirmar o acesso ao portal.
        </Alert>
      ) : null}

      {acessoPortal && !email?.trim() ? (
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          Informe um e-mail na seção Contato para liberar o portal e enviar convites.
        </Alert>
      ) : null}

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={600}
          sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}
        >
          Situação
        </Typography>
        <Chip size="small" label={statusChip.label} color={statusChip.color} variant="outlined" />
        {acessoPortal && liberarSolicitacoes ? (
          <Chip size="small" label="Solicitações" color="primary" variant="outlined" />
        ) : null}
        {acessoPortal && liberarTarefas ? (
          <Chip size="small" label="Tarefas" color="primary" variant="outlined" />
        ) : null}
      </Stack>

      <Divider />

      <TextField
        size="small"
        label="Link público do portal"
        value={portalUrl}
        InputProps={{ readOnly: true }}
        helperText="Compartilhe este endereço ou envie o convite por e-mail."
        fullWidth
      />

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={handleCopy} size="small">
          {copied ? "Link copiado" : "Copiar link"}
        </Button>
        <Button
          variant="contained"
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <MailOutlineIcon />}
          onClick={handleInvite}
          disabled={
            !acessoPortal ||
            !clientId ||
            !email?.trim() ||
            busy ||
            accessSaving ||
            (!liberarSolicitacoes && !liberarTarefas)
          }
          size="small"
        >
          Enviar convite
        </Button>
      </Stack>

      {msg ? (
        <Alert severity="success" sx={{ borderRadius: 2 }}>
          {msg}
        </Alert>
      ) : null}
      {err ? (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {err}
        </Alert>
      ) : null}
    </Stack>
  );

  if (compact || embeddedInTab) {
    return (
      <Box sx={{ mt: embeddedInTab ? 0 : 2 }}>
        {embeddedInTab ? (
          <Typography sx={clientFormSubsectionTitleSx} component="p">
            Portal do Cliente
          </Typography>
        ) : null}
        {inner}
      </Box>
    );
  }

  const cardSx = sectionCardSx || { mb: 2, borderRadius: 2.5 };

  return (
    <Card id="section-portal-cliente" variant="outlined" sx={{ ...cardSx, mb: 2 }}>
      <CardHeader
        title="Portal do Cliente"
        subheader="Área restrita: defina se o cliente usa solicitações, tarefas ou ambos."
        sx={clientFormSectionHeaderSx}
      />
      <CardContent sx={clientFormSectionBodySx}>{inner}</CardContent>
    </Card>
  );
}
